import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Link as LinkIcon, 
  QrCode, 
  BarChart3, 
  Globe, 
  Settings, 
  Search,
  Moon,
  Sun,
  User,
  LogOut,
  Bell,
  ChevronRight,
  Home
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
// import { Separator } from './ui/separator';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { useUIStore } from '../stores/ui-store';
import { useAuth } from '../hooks/useAuth';
import { cn } from '../lib/utils';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: number;
  shortcut?: string;
}

interface BreadcrumbItem {
  label: string;
  href?: string;
}

const navItems: NavItem[] = [
  { id: 'links', label: 'Links', icon: LinkIcon, href: '/dashboard/links', shortcut: 'L' },
  { id: 'qr-codes', label: 'QR Codes', icon: QrCode, href: '/dashboard/qr-codes', shortcut: 'Q' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, href: '/dashboard/analytics', shortcut: 'A' },
  { id: 'domains', label: 'Domains', icon: Globe, href: '/dashboard/domains', shortcut: 'D' },
  { id: 'settings', label: 'Settings', icon: Settings, href: '/dashboard/settings', shortcut: 'S' },
];

export function DashboardLayout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { sidebarOpen, darkMode, toggleSidebar, setSidebarOpen, toggleDarkMode } = useUIStore();
  const [searchValue, setSearchValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const mainContentRef = useRef<HTMLElement>(null);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname, setSidebarOpen]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial state

    return () => window.removeEventListener('resize', handleResize);
  }, [setSidebarOpen]);

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Global search shortcut (Cmd/Ctrl + K)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      // Navigation shortcuts (Cmd/Ctrl + Shift + [key])
      if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
        const navItem = navItems.find(item => item.shortcut === e.key.toUpperCase());
        if (navItem) {
          e.preventDefault();
          window.location.href = navItem.href;
        }
      }

      // Escape key to close sidebar on mobile
      if (e.key === 'Escape' && sidebarOpen && window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen, setSidebarOpen]);

  const handleLogout = () => {
    logout();
  };

  const getUserInitials = (email: string) => {
    return email.split('@')[0].substring(0, 2).toUpperCase();
  };

  const isActiveRoute = (href: string) => {
    if (href === '/dashboard/links' && location.pathname === '/dashboard') {
      return true;
    }
    return location.pathname.startsWith(href);
  };

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Dashboard', href: '/dashboard' }];
    
    const currentNav = navItems.find(item => isActiveRoute(item.href));
    if (currentNav && currentNav.href !== '/dashboard') {
      breadcrumbs.push({ label: currentNav.label });
    }
    
    return breadcrumbs;
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      // TODO: Implement search functionality
      console.log('Search:', searchValue);
    }
  };

  // Loading skeleton component for future use
  // const LoadingSkeleton = () => (
  //   <div className="space-y-4 p-6">
  //     <Skeleton className="h-8 w-48" />
  //     <Skeleton className="h-4 w-96" />
  //     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
  //       <Skeleton className="h-32 w-full" />
  //       <Skeleton className="h-32 w-full" />
  //       <Skeleton className="h-32 w-full" />
  //     </div>
  //   </div>
  // );

  return (
    <div className={cn("flex h-screen bg-gray-50 dark:bg-gray-900", darkMode && "dark")}>
      {/* Skip to main content link for screen readers */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-lg z-50"
      >
        Skip to main content
      </a>
      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        role="navigation"
        aria-label="Main navigation"
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          darkMode && "bg-gray-900 border-gray-700",
          "motion-reduce:transition-none"
        )}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700">
          <Link to="/dashboard" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 dark:text-white">go2url.me</span>
          </Link>
          
          {/* Close button - mobile only */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close-button"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2" role="navigation" aria-label="Primary navigation">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActiveRoute(item.href);
            
            return (
              <Link
                key={item.id}
                to={item.href}
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors group",
                  "motion-reduce:transition-none",
                  isActive
                    ? "bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                )}
                aria-current={isActive ? 'page' : undefined}
                title={`${item.label} (⌘⇧${item.shortcut})`}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1" aria-label={`${item.badge} notifications`}>
                    {item.badge}
                  </span>
                )}
                {item.shortcut && (
                  <span className="ml-auto text-xs opacity-50 group-hover:opacity-100 transition-opacity">
                    ⌘⇧{item.shortcut}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            v1.0.0 • {new Date().getFullYear()}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center justify-between h-full px-6">
            {/* Left side - Hamburger menu and search */}
            <div className="flex items-center space-x-4">
              {/* Hamburger menu */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSidebar}
                className="lg:hidden"
                data-testid="mobile-menu-button"
                aria-label="Toggle sidebar"
              >
                <Menu className="w-5 h-5" />
              </Button>

              {/* Desktop hamburger (collapse sidebar) */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSidebar}
                className="hidden lg:flex"
                data-testid="desktop-menu-button"
                aria-label="Toggle sidebar"
              >
                <Menu className="w-5 h-5" />
              </Button>

              {/* Search Bar */}
              <div className="relative hidden sm:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search links, QR codes... (⌘K)"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  className={cn(
                    "pl-10 w-64 lg:w-80 transition-all duration-200",
                    isSearchFocused && "ring-2 ring-blue-500 dark:ring-blue-400"
                  )}
                  aria-label="Search links and QR codes"
                  role="searchbox"
                />
                {isSearchFocused && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                    ⌘K
                  </div>
                )}
              </div>
            </div>

            {/* Right side - Dark mode toggle, notifications, user menu */}
            <div className="flex items-center space-x-4">
              {/* Dark mode toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleDarkMode}
                className="w-9 h-9"
                data-testid="dark-mode-toggle"
                aria-label="Toggle dark mode"
              >
                {darkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>

              {/* Notifications */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-9 h-9 relative"
                data-testid="notifications-button"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </Button>

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center space-x-2 h-9 px-2"
                    data-testid="user-menu-button"
                    aria-label="User menu"
                  >
                    <Avatar className="w-7 h-7" data-testid="user-avatar">
                      <AvatarFallback className="text-xs">
                        {user?.email ? getUserInitials(user.email) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {user?.email?.split('@')[0] || 'User'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.email?.split('@')[0] || 'User'}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/settings" className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/settings" className="flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Breadcrumbs */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <nav aria-label="Breadcrumb" className="px-6 py-3">
            <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              {getBreadcrumbs().map((crumb, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4 mx-2 text-gray-400" aria-hidden="true" />
                  )}
                  {crumb.href ? (
                    <Link 
                      to={crumb.href} 
                      className="hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                    >
                      {index === 0 && <Home className="w-4 h-4 mr-1" aria-hidden="true" />}
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-gray-900 dark:text-gray-100 font-medium">
                      {crumb.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        {/* Main Content */}
        <main 
          ref={mainContentRef}
          id="main-content"
          className="flex-1 overflow-auto"
          role="main"
          aria-label="Main content"
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300 motion-reduce:transition-none"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setSidebarOpen(false);
            }
          }}
        />
      )}
    </div>
  );
}